import React, {useEffect, useState} from 'react';
import {motion} from 'motion/react';
import {Eye, EyeOff, Lock, Mail} from 'lucide-react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Snackbar,
    TextField,
    ThemeProvider,
    Typography,
    createTheme,
} from '@mui/material';
import logoSrc from '../../res/imgs/logo_black.svg';
import {login} from "./LoginService";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../shared/auth/AuthContext";
import {getBackendWarningMessage} from "../../shared/utils";
import PasswordResetFlow from "./PasswordResetFlow";

export const LoginPage = ({
    primaryColor = "#0f766e",
    accentColor = "#14b8a6",
    companyName = "BusiFarm",
    backgroundIntensity = 0.03
}) => {
    const navigate = useNavigate();
    const [currentScreen, setCurrentScreen] = useState('login');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [warn, setWarn] = useState('');
    const [info, setInfo] = useState('');
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const {authenticated, login: authenticate} = useAuth();

    useEffect(() => {
        if (authenticated) {
            navigate('/', {replace: true});
        }
    }, [authenticated, navigate]);

    const clearFeedback = () => {
        setError('');
        setWarn('');
        setInfo('');
    };

    const handleChange = (event) => {
        const {name, value} = event.target;
        setFormData((previous) => ({
            ...previous,
            [name]: value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        clearFeedback();
        setSubmitting(true);

        try {
            const credentials = {
                email: formData.email.trim(),
                password: formData.password,
            };
            const response = await login(credentials);
            if (!response.data || typeof response.data !== 'object') {
                throw new Error('Resposta de login inválida.');
            }

            authenticate({
                ...response.data,
                email: response.data.email || credentials.email,
            });
            navigate('/', {replace: true});
        } catch (err) {
            const backendMessage = getBackendWarningMessage(err);
            if (backendMessage) {
                setWarn(backendMessage);
            } else {
                setError(err.message || 'Não foi possível realizar o login.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const renderLogoHeader = () => (
        <>
            <Box sx={{display: 'flex', justifyContent: 'center'}}>
                <motion.div
                    whileHover={{scale: 1.05}}
                    transition={{type: "spring", stiffness: 400, damping: 10}}
                    style={{
                        height: 120,
                        width: 120,
                        borderRadius: '50%',
                        background: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src={logoSrc}
                        alt={`${companyName} logo`}
                        sx={{width: '100%', height: '100%', objectFit: 'contain', display: 'block'}}
                    />
                </motion.div>
            </Box>

            <Typography variant="h4" component="h1" sx={{mt: 3, fontWeight: 700}}>
                {companyName}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                Sistema de Gerenciamento Empresarial
            </Typography>
        </>
    );

    const renderLoginScreen = () => (
        <>
            {renderLogoHeader()}

            <Box component="form" onSubmit={handleSubmit} sx={{mt: 1, width: '100%'}}>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Mail size={20}/>
                            </InputAdornment>
                        ),
                    }}
                />

                <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Lock size={20}/>
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword((current) => !current)}
                                    edge="end"
                                >
                                    {showPassword ? <EyeOff size={20}/> : <Eye size={20}/>}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2}}>
                    <FormControlLabel
                        control={<Checkbox color="primary" name="remember-me" id="remember-me"/>}
                        label={<Typography variant="body2">Lembrar-me</Typography>}
                    />

                    <Typography variant="body2">
                        <Box
                            component="button"
                            type="button"
                            onClick={() => {
                                clearFeedback();
                                setCurrentScreen('forgot');
                            }}
                            sx={{
                                border: 0,
                                p: 0,
                                background: 'transparent',
                                color: accentColor,
                                textDecoration: 'none',
                                cursor: 'pointer',
                                font: 'inherit',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Esqueceu sua senha?
                        </Box>
                    </Typography>
                </Box>

                <motion.div whileHover={{scale: 1.02}} whileTap={{scale: 0.98}} style={{marginTop: 24}}>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        sx={{mt: 3, mb: 2, py: 1.5, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'}}
                    >
                        {submitting ? 'Entrando...' : 'Entrar'}
                    </Button>
                </motion.div>
            </Box>
        </>
    );

    const theme = createTheme({
        palette: {
            primary: {
                main: primaryColor,
            },
            secondary: {
                main: accentColor,
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                    },
                },
            },
        },
    });

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 6,
                    px: 2,
                    bgcolor: 'grey.50',
                    backgroundImage: `radial-gradient(${primaryColor} ${backgroundIntensity * 100}%, transparent 0)`,
                    backgroundSize: '20px 20px',
                }}
            >
                <motion.div
                    initial={{opacity: 0, y: -20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.5}}
                    style={{width: '100%', maxWidth: '440px'}}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {currentScreen === 'login' ? (
                            renderLoginScreen()
                        ) : (
                            <PasswordResetFlow
                                primaryColor={primaryColor}
                                accentColor={accentColor}
                                onBackToLogin={() => setCurrentScreen('login')}
                                onError={setError}
                                onWarn={setWarn}
                                onInfo={setInfo}
                                onClearFeedback={clearFeedback}
                            />
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{mt: 3, textAlign: 'center'}}>
                            © {new Date().getFullYear()} {companyName}. Todos os direitos reservados.
                        </Typography>
                    </Paper>
                </motion.div>

                <Snackbar open={!!error} autoHideDuration={3500} onClose={() => setError('')}>
                    <Alert onClose={() => setError('')} severity="error" variant="filled">{error}</Alert>
                </Snackbar>
                <Snackbar open={!!warn} autoHideDuration={3500} onClose={() => setWarn('')}>
                    <Alert onClose={() => setWarn('')} severity="warning" variant="filled">{warn}</Alert>
                </Snackbar>
                <Snackbar open={!!info} autoHideDuration={3000} onClose={() => setInfo('')}>
                    <Alert onClose={() => setInfo('')} severity="info" variant="filled">{info}</Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
}
